import React, { useState, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import { Table, createColumnHelper } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import { Text } from '@bestdoctor/spectrum-uikit/Text';

import { Loader } from 'shared/loaders/Loader';

import {
  ICompanyActionTable,
  TableHistoryProps,
} from 'interfaces/patients/patientInterfaces';

import { getRowChangeString, throttle } from 'utils/tablesMethods';
import { lineBreaker } from 'utils/lineBreaker';

const columnHelper = createColumnHelper<ICompanyActionTable>();

function createData(
  company: string,
  date: string,
  date_action: string,
  user: string,
  action_category: string,
  old_value: string,
  new_value: string,
  full_name: string,
  take_effect_at: string,
  verbose_changed_field_name: string,
  uuid: string
): ICompanyActionTable {
  return {
    company,
    date,
    date_action,
    user,
    action_category,
    old_value,
    new_value,
    full_name,
    take_effect_at,
    verbose_changed_field_name,
    uuid,
  };
}

export const TableHistory: React.FC<TableHistoryProps> = ({
  historyActions,
  hasNextPageCompanyActions,
  isFetchingNextPageCompanyActions,
  isLoadingCompanyActions,
  fetchNextPageCompanyActions,

  orderingTableHistory,
  handleOrderingTableHistory,
}) => {
  const rows = useMemo(
    () =>
      historyActions.map((item) =>
        createData(
          item.company,
          item.date,
          item.date_action,
          item.user,
          item.action_category,
          item.old_value,
          item.new_value,
          item.full_name,
          item.take_effect_at,
          item.verbose_changed_field_name,
          item.uuid
        )
      ),
    [historyActions]
  );

  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  // Синхронизируем внешнюю сортировку с внутренней
  useEffect(() => {
    if (orderingTableHistory) {
      const isDesc = orderingTableHistory.startsWith('-');
      const fieldId = isDesc ? orderingTableHistory.slice(1) : orderingTableHistory;
      setSorting([{ id: fieldId, desc: isDesc }]);
    } else {
      setSorting([]);
    }
  }, [orderingTableHistory]);

  // Обработка изменения сортировки
  const handleSortingChange = useMemo(
    () => (newSorting: { id: string; desc: boolean }[]) => {
      if (newSorting.length > 0) {
        const sort = newSorting[0];
        handleOrderingTableHistory(sort.desc ? `-${sort.id}` : sort.id);
      } else {
        handleOrderingTableHistory('');
      }
    },
    [handleOrderingTableHistory]
  );

  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: (updater: any) => {
        const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
        handleSortingChange(newSorting);
      },
    };
  }, [sorting, handleSortingChange]);

  useEffect(() => {
    const handleScroll = (evt: Event): void => {
      const { scrollHeight, scrollTop } = (evt.target as Document).documentElement;
      const windowHeight = window.innerHeight;

      if (scrollHeight - (windowHeight + scrollTop) < 50) {
        fetchNextPageCompanyActions();
      }
    };

    const throttledHandleScroll = throttle(handleScroll, 1000);

    if (hasNextPageCompanyActions && !isFetchingNextPageCompanyActions) {
      document.addEventListener('scroll', throttledHandleScroll);
    }

    return () => {
      document.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [hasNextPageCompanyActions, isFetchingNextPageCompanyActions, fetchNextPageCompanyActions]);

  const columns = useMemo<ColumnDef<ICompanyActionTable, any>[]>(
    () => [
      columnHelper.accessor('company', {
        id: 'slot__program__service_period__company__title',
        header: () => <Text as="span" variant="body-m">Юр. лицо</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('date_action', {
        id: 'date_action',
        header: () => <Text as="span" variant="body-m">Дата действия</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('user', {
        id: 'user__email',
        header: () => <Text as="span" variant="body-m">Изменения внёс</Text>,
        cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor(
        (row) => getRowChangeString(row.action_category, row.verbose_changed_field_name),
        {
          id: 'category',
          header: () => <Text as="span" variant="body-m">Действие</Text>,
          cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
          enableSorting: true,
          meta: {
            headerCellProps: {
              textVariant: 'body-m',
            },
          },
        }
      ) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('full_name', {
        id: 'slot__patient__last_name',
        header: () => <Text as="span" variant="body-m">ФИО</Text>,
        cell: (info) => <Text as="span" variant="body-m">{lineBreaker(info.getValue())}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('date', {
        id: 'date',
        header: () => <Text as="span" variant="body-m">Дата изменения</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('old_value', {
        id: 'old_value',
        header: () => <Text as="span" variant="body-m">Старые данные</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Text as="span" variant="body-m">
              {row.action_category !== 'D' ? lineBreaker(row.old_value) : '-'}
            </Text>
          );
        },
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
      columnHelper.accessor('new_value', {
        id: 'new_value',
        header: () => <Text as="span" variant="body-m">Новые данные</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Text as="span" variant="body-m">
              {row.action_category !== 'D' ? lineBreaker(row.new_value) : '-'}
            </Text>
          );
        },
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ICompanyActionTable, any>,
    ],
    []
  );

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px',
      }}
    >
      <Table
        data={rows}
        columns={columns}
        sorting={tableSorting}
        enableSorting
      />
      {(isFetchingNextPageCompanyActions || isLoadingCompanyActions) && <Loader />}
    </Box>
  );
};
